import { useQuery } from "@tanstack/react-query";
import { useUserRole } from "@/hooks/use-user-role";
import { getAuthenticatedLocalClient } from "@/lib/supabase-clients";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessSource } from "@/hooks/use-access-source";

export const ASSIGNABLE_PAGES = [
  { key: "reservations", label: "Reservations" },
  { key: "calendar", label: "Calendar" },
  
  { key: "guest-ratings", label: "Guest Ratings" },
  { key: "guest-reviews", label: "Guest Reviews" },
  { key: "am-users", label: "AM: Create Users" },
  { key: "min-rate-calculator", label: "Minimum Rate Calculator" },
  { key: "pm-setup", label: "PM Setup (brand logo)" },
  
  { key: "owner-performance", label: "Owner: Performance" },
  
  
  { key: "net-revenue", label: "Owner: Net Revenue" },
  { key: "glossary", label: "Owner: Docs & Glossary" },
  { key: "links", label: "Owner: Links" },
  { key: "acl", label: "Admin: Access Control" },
  { key: "admin-import-users", label: "Admin: Import Users" },
  { key: "admin-bulk-import", label: "Admin: Bulk CSV Import" },
  { key: "admin-sync-reviews", label: "Admin: Sync Reviews" },
  { key: "admin-change-password", label: "Admin: Change Password" },
  { key: "admin-take-action", label: "Admin: Take Action" },
  { key: "admin-secrets-clients", label: "Admin: Secrets — Clients" },
  { key: "admin-secrets-owners", label: "Admin: Secrets — Owners" },
  { key: "admin-scheduler", label: "Admin: Scheduler" },
] as const;

export type PageKey = (typeof ASSIGNABLE_PAGES)[number]["key"];

function getLocalUserIdFromToken(localToken: string): string | null {
  try {
    const payload = JSON.parse(atob(localToken.split('.')[1]));
    return payload.sub || null;
  } catch {
    return null;
  }
}

export function usePageAccess() {
  const { localToken, user } = useAuth();
  const { isSuperAdmin, isLoading: rolesLoading } = useUserRole();
  const { isPricelabs, isSourceRestricted, isLoading: sourceLoading } = useAccessSource();
  const localUserId = localToken ? getLocalUserIdFromToken(localToken) : null;

  // Non-super-admins resolve their allowed pages via the SECURITY DEFINER
  // RPC `user_has_page_access`. This avoids needing direct SELECT on
  // role_page_access (which is now restricted to super admins).
  const { data: allowedPages = [], isLoading: pagesLoading } = useQuery({
    queryKey: ["user_page_access", localUserId],
    queryFn: async (): Promise<string[]> => {
      const client = getAuthenticatedLocalClient();
      if (!client || !localUserId) return [];
      const results = await Promise.all(
        ASSIGNABLE_PAGES.map(async (p) => {
          const { data, error } = await client.rpc("user_has_page_access", {
            _user_id: localUserId,
            _page: p.key,
          });
          if (error) throw error;
          return data === true ? (p.key as string) : null;
        })
      );
      return results.filter((k): k is string => k !== null);
    },
    enabled: !!localToken && !!localUserId && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const PRICELABS_BLOCKED = new Set(["guest-ratings", "guest-reviews"]);

  const basePages = isSuperAdmin
    ? ASSIGNABLE_PAGES.map((p) => p.key)
    : allowedPages;

  const shouldBlockPricelabsPages = isPricelabs || (isSourceRestricted && sourceLoading);

  const pages = new Set<string>(
    shouldBlockPricelabsPages ? basePages.filter((p) => !PRICELABS_BLOCKED.has(p)) : basePages
  );

  const hasAccess = (page: string) => {
    if (shouldBlockPricelabsPages && PRICELABS_BLOCKED.has(page)) return false;
    return isSuperAdmin || pages.has(page);
  };

  // Wait for the local token before claiming we know the role.
  // Without this, super_admin briefly looks like a no-role user and
  // ProtectedRoute redirects to /auth, causing a Navigate render loop.
  const waitingForLocalToken = !!user && !localToken;

  return {
    pages,
    hasAccess,
    isSuperAdmin,
    isLoading: rolesLoading || pagesLoading || sourceLoading || waitingForLocalToken,
  };
}
